// JoinProgressBadge.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color, textColor } from '../style/variables.js';
import MoreIconSrc from '../../img/ic-arrow-right-xs.svg';
import KYCIdIconSrc from '../../img/ic_kyc_id.svg';
import KYCReviewIconSrc from '../../img/ic_kyc_review.svg';
import KYCSwaggerIconSrc from '../../img/ic_kyc_swagger.svg';
import KYCRejectedIconSrc from '../../img/ic_kyc_rejected.svg';
import ErrorIconSrc from '../../img/chatroom_error.svg';
import ErrorMoreIconSrc from '../../img/ic_arrow_right_red_xs.svg';
import StarIconSrc from '../../img/ic_star.svg';

import { Progress as JoinProgress } from '../resource/joinConstants.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import useBecomeCreatorLink from '../hook/useBecomeCreatorLink.js';

export const BadgeType = {
  DEFAULT: 'default',
  SIMPLE: 'simple',
};

/**
 * Get kyc badge title, description and background color.
 * @param {string} {progress} - join progress.
 * @param {boolean} {isKycFailed} - is kyc failed.
 * @param {boolean} {isSimpleBadge} - is simple badge.
 * @return {object|null} Return badge data.
 */
const getBadge = ({ progress, isKycFailed, isSimpleBadge }) => {
  const defaultBadge = {
    titleI18nKeys: ['swaggers_wanted', 'swaggers_wanted_to_earn'],
    descriptionI18nKey: 'swaggers_wanted_subtitle',
    backgroundColor: 'rgba(255, 209, 0, 0.9)',
    iconSource: StarIconSrc,
    shouldShowMore: true,
  };
  switch (progress) {
    case JoinProgress.PERSONAL_INFORMATION: {
      if (isKycFailed) {
        return {
          titleI18nKey: 'verify_error',
          descriptionI18nKey: 'verify_error_description',
          backgroundColor: 'rgba(207, 4, 4, 0.8)',
          iconSource: KYCRejectedIconSrc,
          shouldShowMore: true,
        };
      }
      if (!isSimpleBadge) {
        return defaultBadge;
      }
      return null;
    }
    case JoinProgress.OCR_PROCESSING: {
      return {
        titleI18nKey: 'verifying_application',
        descriptionI18nKey: 'verifying_processing',
        backgroundColor: 'rgba(96, 196, 232, 0.8)',
        iconSource: KYCIdIconSrc,
        shouldShowMore: false,
      };
    }
    case JoinProgress.OCR_RESULT: {
      return {
        titleI18nKey: 'verifying_application',
        descriptionI18nKey: 'verifying_application_check',
        backgroundColor: 'rgba(96, 196, 232, 0.8)',
        iconSource: KYCIdIconSrc,
        shouldShowMore: true,
      };
    }
    case JoinProgress.MANUAL_REVIEW: {
      return {
        titleI18nKey: 'received_application',
        descriptionI18nKey: 'received_application_description',
        backgroundColor: 'rgba(96, 196, 232, 0.8)',
        iconSource: KYCReviewIconSrc,
        shouldShowMore: false,
      };
    }
    case JoinProgress.BECOME_SWAGGER: {
      return {
        titleI18nKey: 'become_swagger',
        descriptionI18nKey: 'become_swagger_description',
        backgroundColor: 'rgba(0, 210, 190, 0.8)',
        iconSource: KYCSwaggerIconSrc,
        shouldShowMore: true,
      };
    }
    default: {
      if (!isSimpleBadge) {
        return defaultBadge;
      }
      return null;
    }
  }
};

export const JoinProgressBadge = ({
  progress = null,
  badgeType = BadgeType.DEFAULT,
  kycFailedreasons = [],
  location = {},
  push = () => null,
  addModal = () => null,
}) => {
  const isKycFailed = kycFailedreasons.length > 0;
  const isSimpleBadge = BadgeType.SIMPLE === badgeType;
  const badge = getBadge({
    progress,
    isKycFailed,
    isSimpleBadge,
  });
  const { t } = useTranslation();
  const becomeCreatorLink = useBecomeCreatorLink({
    shouldHideIsCreator: false,
  });
  const { isInternalLink, link } = becomeCreatorLink || {};
  const handleKycButtonOnClick = useCallback(() => {
    if (JoinProgress.BECOME_SWAGGER === progress) {
      return addModal({
        id: 'DownloadCreatorModal',
      });
    }
    if (isInternalLink) {
      if (JoinProgress.MANUAL_REVIEW === progress) return null;
      return push({ pathname: `/join/${progress}`, search: location.search });
    }
    return window.open(link, '_blank', 'noopener,noreferrer');
  }, [addModal, isInternalLink, link, location.search, progress, push]);
  if (!badge || !becomeCreatorLink) {
    return null;
  }
  const {
    titleI18nKey,
    titleI18nKeys,
    descriptionI18nKey,
    backgroundColor,
    iconSource,
    shouldShowMore,
  } = badge;
  if (BadgeType.SIMPLE === badgeType) {
    return (
      <StyledSimpleJoinProgressBadge
        data-element_id={ButtonId.Profile.ButtonKycBadge}
        data-tracking_payload={{
          'join.progress': progress,
        }}
        onClick={handleKycButtonOnClick}
        isKycFailed={isKycFailed}
        disabled={!shouldShowMore}
      >
        {isKycFailed && <ErrorIcon src={ErrorIconSrc} alt="error icon" />}
        {t(titleI18nKey, {
          ns: TranslationNamespace.CREATOR_ONBOARDING,
        })}
        {isKycFailed && (
          <ErrorMoreIconWrpper>
            <img src={ErrorMoreIconSrc} alt="become a creator" />
          </ErrorMoreIconWrpper>
        )}
      </StyledSimpleJoinProgressBadge>
    );
  }
  return (
    <StyledJoinProgressBadge
      data-element_id={ButtonId.Profile.ButtonKycBadge}
      data-tracking_payload={{
        'join.progress': progress,
      }}
      onClick={handleKycButtonOnClick}
      disabled={!shouldShowMore}
    >
      {/* Need wrapper since Safari don't support grid in <button> */}
      {/* Ref: https://stackoverflow.com/a/35466231 */}
      <Wrapper backgroundColor={backgroundColor}>
        <KycButtonIcon src={iconSource} alt="kyc icon" aria-hidden />
        <KycButtonTitle>
          {titleI18nKeys
            ? titleI18nKeys.map(key =>
                t(key, {
                  ns: TranslationNamespace.CREATOR_ONBOARDING,
                })
              )
            : t(titleI18nKey, {
                ns: TranslationNamespace.CREATOR_ONBOARDING,
              })}
        </KycButtonTitle>
        <KycButtonContent>
          {t(descriptionI18nKey, {
            ns: TranslationNamespace.CREATOR_ONBOARDING,
          })}
        </KycButtonContent>
        <KycButtonMore shouldShowMore={shouldShowMore}>
          <img src={MoreIconSrc} alt="become a creator" />
        </KycButtonMore>
      </Wrapper>
    </StyledJoinProgressBadge>
  );
};

JoinProgressBadge.propTypes = {
  progress: PropTypes.string,
  badgeType: PropTypes.string,
  kycFailedreasons: PropTypes.array,
  location: PropTypes.object,
  push: PropTypes.func,
  addModal: PropTypes.func,
};

const StyledJoinProgressBadge = styled.button`
  ${ResetButtonStyle};
  width: 100%;
  :disabled {
    cursor: auto;
  }
`;

const Wrapper = styled.div.attrs(({ backgroundColor }) => ({
  style: {
    backgroundColor,
  },
}))`
  padding: 8px 12px;
  color: ${color.white};
  display: grid;
  grid-template-areas:
    'kyc-icon kyc-title more-icon'
    'kyc-icon kyc-content more-icon';
  grid-template-columns: 48px 1fr 20px;
  grid-column-gap: 12px;
  grid-row-gap: 4px;
  align-items: center;
`;

const KycButtonTitle = styled.div`
  grid-area: kyc-title;
  text-align: left;
  color: ${textColor.white};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
`;

const KycButtonContent = styled.div`
  grid-area: kyc-content;
  text-align: left;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.43;
`;

const KycButtonIcon = styled.img`
  grid-area: kyc-icon;
  width: 48px;
  height: 48px;
`;

const KycButtonMore = styled.div.attrs(({ shouldShowMore }) => ({
  style: {
    display: shouldShowMore ? 'flex' : 'none',
  },
}))`
  grid-area: more-icon;
  display: flex;
  align-items: center;
  height: 100%;
  > img {
    width: 24px;
    height: 24px;
  }
`;

const StyledSimpleJoinProgressBadge = styled.button.attrs(
  ({ isKycFailed }) => ({
    style: {
      color: isKycFailed ? textColor.danger : textColor.secondary,
    },
  })
)`
  ${ResetButtonStyle};
  display: flex;
  align-items: flex-start;
  line-height: 20px;
  font-size: 12px;
  :disabled {
    cursor: auto;
  }
`;

const ErrorIcon = styled.img`
  margin-right: 4px;
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const ErrorMoreIconWrpper = styled.div`
  margin-left: 4px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  > img {
    width: 12px;
    height: 12px;
    object-fit: contain;
  }
`;

export default JoinProgressBadge;
